"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function Obfuscate({
  value
}: {
  value: string;
}) {
  const [displayValue, setDisplayValue] = React.useState("");
  React.useEffect(() => {
    setDisplayValue(value);
  }, [value]);
  return <>{displayValue}</>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="98119" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8 text-blue-600" />
        </div>
        <h3 className="text-2xl font-sans font-extrabold text-slate-950">Wiadomość wysłana</h3>
        <p className="text-slate-600">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-blue-600/50 hover:bg-blue-50 text-blue-600">
          Wyślij kolejne zapytanie
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Imię i nazwisko jest wymagane"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-950/80 font-medium">Imię i nazwisko</FormLabel>
              <FormControl>
                <Input {...field} type="text" placeholder="np. Jan Kowalski" className="h-11 rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Adres e-mail jest wymagany",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Nieprawidłowy adres e-mail"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-950/80 font-medium">Adres e-mail</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="jan@kowalski.pl" className="h-11 rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{}} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-950/80 font-medium">Numer telefonu</FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="+48 123 456 789" className="h-11 rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="installation_age" rules={{
        maxLength: {
          value: 4,
          message: "Maksymalnie 4 znaki"
        },
        pattern: {
          value: /^[0-9]{4}$/,
          message: "Podaj rok w formacie RRRR"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-950/80 font-medium">Rok budowy instalacji (np. 2010)</FormLabel>
              <FormControl>
                <Input {...field} type="text" placeholder="2010" maxLength={4} className="h-11 rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="number_of_radiators" rules={{
        min: {
          value: 0,
          message: "Liczba nie może być ujemna"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-950/80 font-medium">Liczba grzejników</FormLabel>
              <FormControl>
                <Input {...field} type="number" placeholder="0" min={0} className="h-11 rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="number_of_underfloor_heating_loops" rules={{
        min: {
          value: 0,
          message: "Liczba nie może być ujemna"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-950/80 font-medium">Liczba obiegów podłogówki</FormLabel>
              <FormControl>
                <Input {...field} type="number" placeholder="0" min={0} className="h-11 rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="approximate_water_volume" rules={{}} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-950/80 font-medium">Przybliżona objętość wody (l)</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 w-full rounded-xl border-slate-200 bg-white/50 focus:ring-blue-600">
                    <SelectValue placeholder="Wybierz objętość..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="50-100">50-100</SelectItem>
                  <SelectItem value="100-150">100-150</SelectItem>
                  <SelectItem value="150-200">150-200</SelectItem>
                  <SelectItem value="200-300">200-300</SelectItem>
                  <SelectItem value="300+">300+</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="ever_cleaned" rules={{}} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-slate-950/80 font-medium">Czy instalacja była już czyszczona?</FormLabel>
              <FormControl>
                <div className="flex gap-6 h-11 items-center">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input type="radio" checked={field.value === "yes"} onChange={() => field.onChange("yes")} className="size-4 text-blue-600 border-slate-300 focus:ring-blue-600 cursor-pointer" />
                    <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">Tak</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input type="radio" checked={field.value === "no"} onChange={() => field.onChange("no")} className="size-4 text-blue-600 border-slate-300 focus:ring-blue-600 cursor-pointer" />
                    <span className="text-sm text-slate-700 group-hover:text-blue-600 transition-colors">Nie</span>
                  </label>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Treść zapytania jest wymagana"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-950/80 font-medium">Treść zapytania</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Opisz swoją instalację lub rodzaj problemu (np. płukanie grzejników, czyszczenie podłogówki)..." className="min-h-[120px] rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_information" rules={{}} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-950/80 font-medium">Dodatkowe informacje</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Wpisz wszelkie dodatkowe uwagi lub pytania" className="min-h-[80px] rounded-xl border-slate-200 bg-white/50 focus-visible:ring-blue-600 focus-visible:border-blue-600 transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold tracking-tight bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20 transition-all rounded-xl">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Wysyłanie...
          </> : "Wyślij zapytanie o darmową wycenę"}
      </Button>
    </Form>;
}
export default function ZnajdzNasIZapytaj() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: mapRef,
    isVisible: isMapVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section id="porozmawiajmy-o-twoim-ogrzewaniu" className="relative py-24 bg-slate-50 overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-blue-400/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-cyan-400/10 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-sans font-extrabold text-slate-950 mb-4 tracking-tight">
            Porozmawiajmy o Twoim ogrzewaniu
          </h2>
          <p className="text-lg text-slate-600">Zadbaj o wydajność swojego systemu CO i obniż rachunki. Skontaktuj się, aby umówić profesjonalne płukanie instalacji.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div ref={mapRef} className={`lg:col-span-5 space-y-8 transition-all duration-700 delay-100 transform ${isMapVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="bg-white/60 backdrop-blur-md border border-slate-200 rounded-3xl p-6 shadow-2xl shadow-slate-200/50">
              <h3 className="text-xl font-sans font-bold text-slate-950 mb-6 border-b border-slate-100 pb-4">
                Dane Kontaktowe
              </h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <div className="mt-1 bg-blue-100 p-2 rounded-xl text-blue-600">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-950">Adres Serwisu</p>
                    <p className="text-slate-600 mt-1">Pszczyna</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="mt-1 bg-blue-100 p-2 rounded-xl text-blue-600">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-950">Telefon</p>
                    <p className="text-slate-600 mt-1"><Obfuscate value="+48 796 280 315" /></p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="mt-1 bg-blue-100 p-2 rounded-xl text-blue-600">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-950">E-mail</p>
                    <p className="text-slate-600 mt-1"><Obfuscate value="kontakt@hydroflush.pl" /></p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="mt-1 bg-blue-100 p-2 rounded-xl text-blue-600">
                    <Clock className="size-5" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-950">Godziny pracy</p>
                    <p className="text-slate-600 mt-1">Pon-Pt: 8:00 - 18:00, Sob: 9:00 - 14:00</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl h-[300px] relative bg-slate-200">
              <iframe title="Lokalizacja Hydroflush Pszczyna" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d20503.24584742618!2d18.93175865!3d49.9785834!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4716b9b3f3b9b3b3%3A0x400f1234567890!2sPszczyna!5e0!3m2!1sen!2spl!4v1700000000000!5m2!1sen!2spl" width="100%" height="100%" style={{
              border: 0,
              filter: "grayscale(100%) contrast(1.1) opacity(0.9)"
            }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0"></iframe>
            </div>
          </div>

          <div ref={formRef} className={`lg:col-span-7 transition-all duration-700 delay-200 transform ${isFormVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="bg-white/80 backdrop-blur-xl border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-2xl shadow-slate-200/50 relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-blue-600/50 to-transparent opacity-50" />
              
              <div className="mb-8">
                <h3 className="text-2xl font-sans font-extrabold text-slate-950 mb-2 tracking-tight">
                  hydroflush – Czysta instalacja – niższe rachunki za ogrzewanie
                </h3>
                <p className="text-slate-600">
                  Wypełnij formularz – oddzwonimy w ciągu kilku godzin, aby omówić szczegóły i zaproponować termin serwisu.
                </p>
              </div>

              <FormProvider formId="heating_discussion_form_contact" sectionName="porozmawiajmy-o-twoim-ogrzewaniu" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: "",
              number_of_radiators: 0,
              number_of_underfloor_heating_loops: 0,
              approximate_water_volume: "",
              installation_age: "",
              ever_cleaned: "",
              additional_information: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              },
              installation_age: {
                maxLength: 4,
                pattern: "^[0-9]{4}$"
              },
              number_of_radiators: {
                min: 0
              },
              number_of_underfloor_heating_loops: {
                min: 0
              }
            }} submitText="Wyślij zapytanie" successMessage="Dziękujemy! Twoja wiadomość została wysłana. Skontaktujemy się z Tobą niebawem." errorMessage="Wystąpił błąd podczas wysyłania. Prosimy o kontakt telefoniczny.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}